/* eslint-disable react/jsx-props-no-spreading */
import React from 'react'
import PropTypes from 'prop-types'
import { Row } from '@sainsburys-tech/bolt'
import { agnostify } from '@sainsburys-tech/boltui-utils'
import { defaultImgParamsTrans } from '../../../helpers/ComponentHelper'
import * as Styled from './M014.styles'

/**
  ** M014 is a one column promo with legal copy. It is used to promote Unique Selling Points to consumers, for example:
  - Fast track
  - Credit
  - Free returns
 */

const M014 = ({
  altText,
  backgroundColor,
  backgroundImageUrl,
  bodyCopy,
  consumingGridType,
  heading,
  legalCopy,
  linkTarget,
  linkTitle,
  linkUrl,
  mainImage,
  subheading,
  onLinkClick,
  textColor
}) => {
  const gridProps = { gutterValue: 0, stack: 'sm', row: 'xs' }

  const backgroundImageStyle = backgroundImageUrl ? `url('${backgroundImageUrl}${defaultImgParamsTrans}')` : null
  const imageStyle = mainImage ? `url('${mainImage}')` : null
  const onLinkClickFn = (e) => (typeof onLinkClick === 'function' ? onLinkClick(e, agnostify(linkUrl)) : false)
  let Wrapper
  let wrapperProps = {
    backgroundColor,
    backgroundImageStyle,
    consumingGridType,
    gutterValue: 0,
    stack: 'xs'
  }
  if (linkUrl) {
    Wrapper = Styled.WrapperLink
    wrapperProps = {
      ...wrapperProps,
      onClick: onLinkClickFn,
      title: linkTitle && linkTitle,
      href: agnostify(linkUrl && linkUrl),
      forwardedAs: 'a'
    }
  } else {
    Wrapper = Styled.WrapperDiv
  }

  const legalMarkup = () => ({ __html: legalCopy })
  const legalNoMarkupForFlex = () => legalCopy.replace(/<{1}[^<>]{1,}>{1}/g, '')
  const subheadingMarkup = () => ({ __html: subheading })
  return (
    <Styled.OuterWrapper data-test='m014-wrapper'>
      <Wrapper data-test='m014-link' target={linkTarget} {...wrapperProps}>
        <Styled.GridWrapper {...gridProps}>
          {mainImage && (
            <Styled.ImageWrapper
              data-test='m014-image-wrapper'
              imageStyle={imageStyle}
              role='img'
              aria-label={altText}
            />
          )}
          <Styled.CopyWrapper data-test='m014-copy-wrapper' block='xs' gutterValue={0}>
            <Styled.HeaderWrapper data-test='m014-heading-wrapper' block='xs' backgroundColor={backgroundColor}>
              {heading && (
                <Styled.HeaderCopy data-test='m014-heading' textColor={textColor}>
                  {heading}
                </Styled.HeaderCopy>
              )}
              {subheading && (
                <Styled.SubHeading
                  data-test='m014-subheading'
                  textColor={textColor}
                  dangerouslySetInnerHTML={subheadingMarkup()}
                />
              )}
              {bodyCopy && (
                <Styled.BodyCopy data-test='m014-bodyCopy' textColor={textColor}>
                  {bodyCopy}
                </Styled.BodyCopy>
              )}
            </Styled.HeaderWrapper>
            {legalCopy && (
              <Row hidden='xs' block='sm' gutterValue={0}>
                <Styled.LegalCopy
                  data-test='m014-legalCopy-SEO'
                  textColor={textColor}
                  backgroundColor={backgroundColor}
                  isBlock
                >
                  {legalNoMarkupForFlex()}
                </Styled.LegalCopy>
              </Row>
            )}
          </Styled.CopyWrapper>
        </Styled.GridWrapper>
        {legalCopy && (
          <Row hidden='sm' block='xs' gutterValue={0}>
            <Styled.LegalCopy
              data-test='m014-legalCopy-SEO2'
              textColor={textColor}
              backgroundColor={backgroundColor}
              isBlock
            >
              {legalNoMarkupForFlex()} /
            </Styled.LegalCopy>
          </Row>
        )}
      </Wrapper>
      {legalCopy && (
        <Row block='xs' gutterValue={0}>
          <Styled.LegalCopy
            data-test='m014-legalCopy'
            textColor={textColor}
            backgroundColor={backgroundColor}
            isBlock={false}
            dangerouslySetInnerHTML={legalMarkup()}
          />
        </Row>
      )}
    </Styled.OuterWrapper>
  )
}

M014.propTypes = {
  /** Required alt text for the image */
  altText: PropTypes.string.isRequired,
  /** Color of the background, white by default */
  backgroundColor: PropTypes.string,
  /** Optional background image url */
  backgroundImageUrl: PropTypes.string,
  /** Optional body copy text */
  bodyCopy: PropTypes.string,
  /** Information about parent type */
  consumingGridType: PropTypes.string,
  /** Optional text for heading */
  heading: PropTypes.string,
  /** Optional legalCopy rich text for more information regarding the promo */
  legalCopy: PropTypes.string,
  /** Option of opening in a new tab, defaults to an empty string  */
  linkTarget: PropTypes.oneOf(['', '_blank', '_self']),
  /** Optional link url */
  linkUrl: PropTypes.string,
  /** Optional link title, can be seen when hovering over the container */
  linkTitle: PropTypes.string,
  /** Optional image */
  mainImage: PropTypes.string,
  /** Optional rich text for subheading */
  subheading: PropTypes.string,
  /** Optional function to handle the click anywhere inside the container */
  onLinkClick: PropTypes.func,
  /** Colour of the text, black by default */
  textColor: PropTypes.string
}

M014.defaultProps = {
  altText: '',
  backgroundColor: 'white',
  backgroundImageUrl: '',
  bodyCopy: '',
  consumingGridType: '',
  heading: '',
  legalCopy: '',
  linkTarget: '',
  linkTitle: '',
  linkUrl: '',
  mainImage: '',
  subheading: '',
  onLinkClick: null,
  textColor: 'black'
}

export default M014
